import {Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy} from '@angular/core';
import {IPageableProjects, IProject} from '../../models';
import {LoadingSpinnerService, ProjectService} from '../../services';
import {TranslateService} from '@ngx-translate/core';
import {ConstructionCosts, ProjectClassification} from '../../enums';
import {BehaviorSubject, debounceTime, Subject, Subscription} from 'rxjs';
import {LocalStorage} from 'ngx-webstorage';
import {ActivatedRoute, Router} from "@angular/router";
import {isoDateFormat} from "../../utils";

@Component({
    selector: 'app-page-projects',
    templateUrl: './page-projects.component.html',
    styleUrls: ['./page-projects.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class PageProjectsComponent implements OnInit, OnDestroy {
    private projectService = inject(ProjectService);
    private translateService = inject(TranslateService);
    private router = inject(Router);
    private activeRoute = inject(ActivatedRoute);

    loadingSpinnerService = inject(LoadingSpinnerService);
    public projects = new BehaviorSubject<IProject[]>([]);
    public projectsLength = -1;

    public currentPageData: IPageableProjects;
    @LocalStorage()
    public searchValue: string;
    public currentPage = 0;

    public statuses = [];
    public classifications = [
        this.classificationStatus(ProjectClassification.INITIAL),
        this.classificationStatus(ProjectClassification.RED),
        this.classificationStatus(ProjectClassification.ORANGE),
        this.classificationStatus(ProjectClassification.GREEN)
    ];

    public constructionCostOptions = [
        this.constructionCosts(ConstructionCosts.LESS_THAN_10M),
        this.constructionCosts(ConstructionCosts.BETWEEN_10M_AND_25M),
        this.constructionCosts(ConstructionCosts.BETWEEN_25M_AND_50M),
        this.constructionCosts(ConstructionCosts.GREATER_THAN_50M)
    ];

    public tableConfig = {
        columns: [
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.name`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.company`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.client`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.construction_costs`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.tender_costs`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.height_above`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.height_under`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.bvo`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.tender_date`)},
            {header: this.translateService.instant(`PAGE_PROJECTEN.table.column.flag`),
                showOnAllPage: true
            }
        ]
    };
    private pageSize = 10;
    @LocalStorage()
    public companyNames?: string[];
    private searchValueSubject: Subject<string>;
    @LocalStorage()
    public searchClassifications: string[];
    @LocalStorage()
    public searchCompanyName?: string;
    @LocalStorage()
    public searchStatuses?: string[];
    @LocalStorage()
    public searchConstructionCosts?: string;
    @LocalStorage()
    public searchStartDate?: string;
    @LocalStorage()
    public searchEndDate?: string;
    public isAllProjectsPage = false;
    private subscriptions: Subscription[] = [];
    public isEmailNotificationSend = false;

    ngOnInit(): void {
        // should be first, before getting the projects
        this.isAllProjectsPage = this.activeRoute.snapshot.url[0].path === 'projecten_all';

        // should be before getting the projects, since we set the start/end dates
        this.initSearchDates();

        this.loadProjects();

        this.searchValueSubject = new Subject<string>();
        this.subscriptions.push(
            this.searchValueSubject
                .pipe(
                    debounceTime(400)
                ).subscribe(value => {
                this.searchValue = value;
                this.loadProjects();
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public searchStatusChanged(statuses: { id: string; name: string }[]) {
        this.searchStatuses = statuses.map(status => status.id);
        this.loadProjects();
    }

    public searchCategoryChanged(classifications: { id: string; name: string }[]) {
        this.searchClassifications = classifications.map(status => status.id);
        this.loadProjects();
    }

    public searchDateChanged() {
        this.loadProjects();
    }

    public searchConstructionCostsChanged(constructionCosts: { id: string; name: string }) {
        if (constructionCosts) {
            this.searchConstructionCosts = constructionCosts.id;
        } else {
            this.searchConstructionCosts = null;
        }

        this.loadProjects();
    }

    public search(value: string) {
        this.searchValueSubject.next(value);
    }

    public setPage(pageNumber: number): void {
        this.currentPage = pageNumber - 1;
        this.loadProjects();
    }

    public firstItem(): number {
        if (this.currentPageData?.page) {
            return (this.currentPageData.page.number * this.pageSize) + 1;
        } else {
            return 0;
        }
    }

    public lastItem(): number {
        if (this.currentPageData?.page) {
            return Math.min((this.currentPageData.page.number + 1) * this.pageSize, this.currentPageData.page.totalElements);
        } else {
            return 0;
        }
    }

    public hasSearchFilter() {
        return (this.searchClassifications && this.searchClassifications.length > 0)
            || (this.searchCompanyName && this.searchCompanyName.length > 0)
            || (this.searchStatuses && this.searchStatuses.length > 0)
            || (this.searchValue && this.searchValue.length > 0);
    }

    public onProjectClick(project: IProject) {
        this.router.navigate(['/project', project.id]);
    }

    private loadProjects() {
        this.loadingSpinnerService.show();
        this.projectService.getProjects(
            this.currentPage,
            this.pageSize,
            this.searchValue,
            this.searchCompanyName,
            this.searchStatuses,
            this.searchClassifications,
            this.searchConstructionCosts,
            this.searchStartDate,
            this.searchEndDate,
            this.isAllProjectsPage
        ).then((pageableProjects) => {
            const content = pageableProjects.content;
            this.projectsLength = content?.length ?? 0;
            this.projects.next(content);
            this.currentPageData = pageableProjects;

            this.loadingSpinnerService.hide();
        });
    }

    private classificationStatus(classification: ProjectClassification) {
        return {id: classification, name: this.translateService.instant(`CLASSIFICATION.${classification}`)};
    }

    private constructionCosts(constructionCosts: ConstructionCosts) {
        return {
            id: constructionCosts,
            name: this.translateService.instant(`FILTER_CONSTRUCTION_COSTS.${constructionCosts}`)
        };
    }

    private initSearchDates() {
        if (!this.searchStartDate || !this.searchEndDate) {
            const firstDay = new Date();
            firstDay.setDate(1);

            const lastDay = new Date();
            lastDay.setMonth(lastDay.getMonth() + 1);
            lastDay.setDate(0);

            this.searchStartDate = (this.searchStartDate) ? this.searchStartDate : isoDateFormat(firstDay);
            this.searchEndDate = (this.searchEndDate) ? this.searchEndDate : isoDateFormat(lastDay);
        }
    }

    protected async triggerEmailNotification() {
        this.isEmailNotificationSend = true;

        await this.projectService.triggerEmailNotification()
            .catch((error) => {
                console.error(error);
            });
    }
}
